import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    ArrowLeft,
    ArrowLeftRight,
    Check,
    Pencil,
    Printer,
    Trash2,
    X,
} from 'lucide-react'
import { toast } from 'sonner'

import type { Search } from './index'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { useAuth } from '@/hooks/use-auth'
import {
    useApproveProductLotTransferNote,
    useCancelProductLotTransferNote,
    useDeleteProductLotTransferNote,
    useRejectProductLotTransferNote,
} from '@/hooks/use-product-lot-mutation'
import { useProductLotTransferNoteQuery } from '@/hooks/use-product-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/transfer-notes/$id',
)({
    component: ProductLotTransferDetailPage,
})

export function ProductLotTransferDetailPage() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { data, isLoading } = useProductLotTransferNoteQuery(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApproveProductLotTransferNote()
    const reject = useRejectProductLotTransferNote()
    const cancel = useCancelProductLotTransferNote()
    const del = useDeleteProductLotTransferNote()
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const approveWorkflow = useWorkflowTracking('product_transfer', {
        autoStart: false,
        attemptKey: id,
        operation: 'approve',
    })
    const rejectWorkflow = useWorkflowTracking('product_transfer', {
        active: rejectOpen,
        attemptKey: id,
        operation: 'reject',
    })
    const cancelWorkflow = useWorkflowTracking('product_transfer', {
        autoStart: false,
        attemptKey: id,
        operation: 'cancel',
    })
    const deleteWorkflow = useWorkflowTracking('product_transfer', {
        autoStart: false,
        attemptKey: id,
        operation: 'delete',
    })

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isPending = note.status === 'pending'
    const isCreator = !!user?.id && note.created_by_id === user.id
    const canApprove = hasPermission('product_lots.approve_transfer')
    const canWrite = hasPermission('product_lots.write')

    const handleApprove = async () => {
        approveWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_approve_out())) {
            approveWorkflow.finish('closed')
            return
        }
        approveWorkflow.step('warning_confirmed')
        try {
            await approveWorkflow.run(() => approve.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : m.sl_detail_error_approve(),
            )
        }
    }

    const handleReject = async () => {
        try {
            await rejectWorkflow.run(() =>
                reject.mutateAsync({ id, reason: rejectReason }),
            )
            setRejectOpen(false)
            setRejectReason('')
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_reject(),
            )
        }
    }

    const handleCancel = async () => {
        cancelWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_cancel_out())) {
            cancelWorkflow.finish('closed')
            return
        }
        cancelWorkflow.step('warning_confirmed')
        try {
            await cancelWorkflow.run(() => cancel.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    const handleDelete = async () => {
        deleteWorkflow.step('warning_shown')
        if (!confirm(m.pdc_confirm_delete())) {
            deleteWorkflow.finish('closed')
            return
        }
        deleteWorkflow.step('warning_confirmed')
        try {
            await deleteWorkflow.run(() => del.mutateAsync(id))
            void navigate({
                to: '/sales-warehouse/transfer-notes',
                search: { page: 1 },
            })
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PageBackButton asChild className="print:hidden">
                                <Link
                                    to="/sales-warehouse/transfer-notes"
                                    search={
                                        recallListSearch<Search>(
                                            LIST_SEARCH_KEYS.transferNotes,
                                        ) ?? {}
                                    }
                                >
                                    <ArrowLeft className="size-4" />
                                </Link>
                            </PageBackButton>
                            <ArrowLeftRight className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <ProductLotStatusBadge status={note.status} />
                        </div>
                        <div className="flex gap-2 flex-wrap print:hidden">
                            <Button
                                variant="outline"
                                className="h-11 md:h-9"
                                onClick={() => window.print()}
                            >
                                <Printer className="size-4 mr-1" />
                                {m.pdc_action_print()}
                            </Button>
                            {isPending && (
                                <>
                                    {isCreator && canWrite && (
                                        <Button
                                            asChild
                                            variant="outline"
                                            className="h-11 md:h-9"
                                        >
                                            <Link
                                                to="/sales-warehouse/transfer-notes/$id/edit"
                                                params={{ id }}
                                            >
                                                <Pencil className="size-4 mr-1" />
                                                {m.pdc_action_edit()}
                                            </Link>
                                        </Button>
                                    )}
                                    {canApprove && (
                                        <>
                                            <Button
                                                className="h-11 md:h-9"
                                                onClick={handleApprove}
                                                disabled={approve.isPending}
                                            >
                                                <Check className="size-4 mr-1" />
                                                {m.pdc_action_approve()}
                                            </Button>
                                            <Button
                                                className="h-11 md:h-9"
                                                variant="destructive"
                                                onClick={() =>
                                                    setRejectOpen(true)
                                                }
                                            >
                                                <X className="size-4 mr-1" />
                                                {m.pdc_action_reject()}
                                            </Button>
                                        </>
                                    )}
                                    {isCreator && (
                                        <>
                                            <Button
                                                className="h-11 md:h-9"
                                                variant="outline"
                                                onClick={handleCancel}
                                                disabled={cancel.isPending}
                                            >
                                                {m.pdc_action_cancel()}
                                            </Button>
                                            <Button
                                                className="h-11 md:h-9"
                                                variant="ghost"
                                                onClick={handleDelete}
                                                disabled={del.isPending}
                                            >
                                                <Trash2 className="size-4 mr-1 text-destructive" />
                                                {m.pdc_action_delete()}
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={
                                    note.status === 'approved'
                                        ? m.sl_detail_label_approver()
                                        : m.sl_detail_label_rejecter()
                                }
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.pdc_field_source_lot()}</TableHead>
                            <TableHead>{m.pll_detail_col_product()}</TableHead>
                            <TableHead>
                                {m.pdc_field_current_position()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.pdc_move_arrow()}
                            </TableHead>
                            <TableHead>{m.pdc_field_dest_position()}</TableHead>
                            <TableHead className="text-right">
                                {m.pdc_field_quantity()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-sm">
                                    <Link
                                        to="/sales-warehouse/lots/$id"
                                        params={{
                                            id: it.source_product_lot_id,
                                        }}
                                        className="text-primary hover:underline"
                                    >
                                        {it.source_product_lot_code ??
                                            it.source_product_lot_id}
                                    </Link>
                                </TableCell>
                                <TableCell>{it.product_name ?? '—'}</TableCell>
                                <TableCell className="font-mono text-sm">
                                    {it.source_position_code ?? '—'}
                                </TableCell>
                                <TableCell className="text-center text-muted-foreground">
                                    {m.pdc_move_arrow()}
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                    {it.dest_position_code ?? '—'}
                                    {it.dest_product_lot_code &&
                                    it.dest_product_lot_id !==
                                        it.source_product_lot_id ? (
                                        <span className="block text-xs text-muted-foreground">
                                            {it.dest_product_lot_code}
                                        </span>
                                    ) : null}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)}
                                    {it.product_unit
                                        ? ` ${it.product_unit}`
                                        : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.sl_detail_dialog_reject_title_out()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>{m.sl_detail_dialog_reject_label()}</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => {
                                rejectWorkflow.interact('details')
                                setRejectReason(e.target.value)
                            }}
                            placeholder={m.sl_detail_dialog_reject_placeholder()}
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={() => setRejectOpen(false)}
                        >
                            {m.sl_detail_dialog_cancel_btn()}
                        </Button>
                        <Button
                            variant="destructive"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={handleReject}
                            disabled={reject.isPending}
                        >
                            {m.sl_detail_dialog_confirm_reject_btn()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
